import { useState, type ChangeEvent } from 'react';
import { EmptyState } from '../components/States';
import { useTranslation } from 'react-i18next';
import type { DispatchImportRowResult, Location } from '@teo/shared';
import { api } from '../api/client';
import { addToCart, CartLines, cartTotal, cartUnits, StockSearch, type CartLine } from '../components/Operation';
import { useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatMoney, formatNumber } from '../lib/format';
import { parseDispatchWorkbook, readFileBytes } from '../lib/inventoryExcel';
import type { Language } from '../i18n';

/** Despacho por referencia: Bodega → local, o ubicación distribuidora → otros locales. Queda en tránsito hasta recibir. */
export function DispatchPanel({
  idPrefix, originId, destinations, header, onDispatched,
}: {
  idPrefix: 'dispatch' | 'masterDispatch';
  originId: number | null;
  destinations: Location[];
  header: React.ReactNode;
  onDispatched: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [destination, setDestination] = useState<number | null>(null);
  const [lines, setLines] = useState<CartLine[]>([]);
  const [searchKey, setSearchKey] = useState(0);
  const options = destinations.filter((l) => l.active && l.type === 'STORE' && l.id !== originId);
  const dest = destination ?? options[0]?.id ?? null;
  const scope = `despacho:${originId}`;
  const [fileName, setFileName] = useState('');
  const [imported, setImported] = useState<DispatchImportRowResult[] | null>(null);

  // Despacho masivo: el archivo se valida en el servidor y las filas válidas se cargan al carrito;
  // el despacho se confirma con el mismo botón y pasa por las mismas reglas que el manual.
  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !originId) return;
    const parsed = parseDispatchWorkbook(await readFileBytes(f));
    setFileName(f.name);
    setImported(null);
    if (parsed.rows.length === 0) return show(t('dispatchImport.noRows'));
    try {
      setImported(await api.importDispatchPreview(originId, parsed.rows));
    } catch (err) {
      showError(err);
    }
  };
  const importErrors = imported?.filter((r) => r.status === 'error') ?? [];
  const importOk = imported?.filter((r) => r.status === 'ok') ?? [];
  const loadImported = () => {
    setLines(importOk.map((r) => ({
      variant_id: r.variant_id!, ref: r.ref, product_name: r.product_name ?? '', color: r.color, size: r.size, qty: r.qty ?? 1,
      unit_price: r.unit_price, suggested: r.unit_price, min_price: 0, available: r.available ?? 0,
    })));
    setImported(null);
    show(t('dispatchImport.loaded', { n: importOk.length }));
  };

  const checkout = () =>
    run(async () => {
      if (!originId || !dest) return;
      try {
        const r = await api.createShipment(requestKey(scope), {
          origin_id: originId, destination_id: dest,
          lines: lines.map((l) => ({ variant_id: l.variant_id, qty: l.qty, unit_price: l.unit_price })),
        });
        releaseKey(scope);
        setLines([]);
        setSearchKey((k) => k + 1);
        show(t('ops.dispatchDone', { doc: r.doc_no }));
        onDispatched();
      } catch (e) {
        showError(e);
      }
    });

  return (
    <>
      <div className="dispatch-upload">
        <div className="panel">
          <div className="head">
            <div>
              <h3>{t('dispatchImport.title')}</h3>
              <p>{t('dispatchImport.help')}</p>
            </div>
            <a className="btn sm" id={`${idPrefix}Template`} href="/plantillas/Modelo_Despacho_Locales_TEO.xlsx" download>
              {t('dispatchImport.template')}
            </a>
          </div>
          <div className="filezone">
            <b>{t('dispatchImport.columns')}</b>
            <br />
            <span className="tiny muted">{t('dispatchImport.columnsHelp')}</span>
            <input id={`${idPrefix}ImportFile`} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => void onFile(e)} />
          </div>
        </div>
        <div className="panel dispatch-preview">
          <div className="head">
            <div>
              <h3>{t('dispatchImport.previewTitle')}</h3>
              <p>{t('dispatchImport.previewHelp')}</p>
            </div>
          </div>
          <div id={`${idPrefix}ImportPreview`}>
            {!imported ? <EmptyState icon="upload" title={t('dispatchImport.empty')} hint={t('dispatchImport.emptyHint')} /> : null}
            {imported ? (
              <>
                <div className="v52-note">{t('dispatchImport.summary', { name: fileName, ok: importOk.length, errors: importErrors.length, units: formatNumber(importOk.reduce((a, r) => a + (r.qty ?? 0), 0), lang) })}</div>
                {importErrors.length > 0 ? (
                  <div className="alerts" style={{ marginTop: 8 }}>
                    {importErrors.slice(0, 30).map((r) => (
                      <div className="alert" key={`${r.row}`} data-error-row={r.row ?? ''}>
                        <div>
                          <b>{t('dispatchImport.rowRef', { row: r.row, ref: r.ref })}</b>
                          <small>{t(`errors.${r.reason}`, { defaultValue: r.reason ?? '', available: r.available ?? 0, requested: r.qty ?? 0, ref: r.ref, color: r.color ?? '', size: r.size ?? '' })}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                <button type="button" className="btn black" style={{ marginTop: 10, width: '100%' }} id={`${idPrefix}ImportApply`} disabled={importErrors.length > 0 || importOk.length === 0} onClick={loadImported}>
                  {t('dispatchImport.apply')}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    <div className="pos">
      <div className="scan">
        {header}
        <div style={{ marginBottom: 8 }}>
          <select className="select" id={`${idPrefix}Store`} style={{ width: '100%' }} value={dest ?? ''} onChange={(e) => setDestination(Number(e.target.value))}>
            {options.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <StockSearch key={searchKey} idPrefix={idPrefix} locationId={originId} placeholder={t('ops.searchPlaceholder')} onPick={(row) => setLines((ls) => addToCart(ls, row, row.internal_price ?? 0))} />
      </div>
      <div className="panel">
        <div className="head">
          <div>
            <h3>{t('ops.dispatchTitle')}</h3>
            <p>{t('ops.dispatchHelp')}</p>
          </div>
          <button type="button" className="btn sm" id={`${idPrefix}Clear`} onClick={() => setLines([])}>
            {t('ops.clear')}
          </button>
        </div>
        <CartLines idPrefix={idPrefix} lines={lines} onChange={setLines} editablePrice priceLabel={t('ops.internalPrice')} />
        <div className="total">
          <div className="totalrow">
            <span>{t('ops.units')}</span>
            <b id={`${idPrefix}Units`}>{formatNumber(cartUnits(lines), lang)}</b>
          </div>
          <div className="totalrow">
            <span>{t('ops.internalValue')}</span>
            <strong id={`${idPrefix}Total`}>{formatMoney(cartTotal(lines), lang)}</strong>
          </div>
          <button type="button" className="btn green" id={`${idPrefix}Checkout`} disabled={lines.length === 0 || !dest || busy} onClick={() => void checkout()}>
            {t('ops.dispatchCheckout')}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
